// ============================================================
// Mon Profil — Console Super Admin (thème clair)
// ============================================================
import React, { useState, useRef } from 'react';
import {
  User, Mail, Phone, ShieldCheck, Camera, Trash2, Loader, KeyRound,
  Save, Clock, CalendarDays, Copy, CheckCircle2,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import superAdminApi from '../../services/superAdminApi';
import { useSuperAdminAuth } from '../../context/SuperAdminAuthContext';
import PhoneInput from '../../components/common/PhoneInput';
import { resolveUploadUrl, initiales } from '../../utils/media';
import toast from 'react-hot-toast';

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function Carte({ icon: Icon, titre, sousTitre, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6 shadow-sm">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-blue-50 text-blue-600">
          <Icon size={20} />
        </div>
        <div>
          <h2 className="text-base font-semibold text-slate-900">{titre}</h2>
          {sousTitre && <p className="text-xs text-slate-500 mt-0.5">{sousTitre}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Champ({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  'w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors';

// ── Bloc « Double authentification (2FA) » — Super Admin (thème clair) ──────
const champCls2fa = 'w-full bg-slate-100 border border-slate-300 text-slate-900 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-600';

function Securite2FASA() {
  const { superAdmin, refreshSuperAdmin } = useSuperAdminAuth();
  const actif = superAdmin?.deux_facteurs_actif === true;
  const [etat, setEtat] = useState('idle');      // idle | setup | codes
  const [secret, setSecret] = useState('');
  const [uri, setUri] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [pwdDisable, setPwdDisable] = useState('');

  const copier = (txt) => { try { navigator.clipboard.writeText(txt); toast.success('Copié'); } catch { /* ignore */ } };

  const demarrer = async () => {
    setLoading(true);
    try {
      const { data } = await superAdminApi.post('/superadmin/auth/2fa/setup');
      setSecret(data.secret || ''); setUri(data.uri || ''); setCode(''); setEtat('setup');
    } catch (e) { toast.error(e.response?.data?.message || 'Erreur'); }
    finally { setLoading(false); }
  };

  const verifier = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return toast.error('Saisis le code à 6 chiffres.');
    setLoading(true);
    try {
      const { data } = await superAdminApi.post('/superadmin/auth/2fa/verify', { code });
      setCodes(data.codesSecours || []); setEtat('codes');
      await refreshSuperAdmin();
    } catch (e) { toast.error(e.response?.data?.message || 'Code invalide.'); }
    finally { setLoading(false); }
  };

  const desactiver = async (e) => {
    e.preventDefault();
    if (!pwdDisable) return toast.error('Mot de passe requis.');
    setLoading(true);
    try {
      await superAdminApi.post('/superadmin/auth/2fa/disable', { password: pwdDisable });
      toast.success('Double authentification désactivée.');
      setShowDisable(false); setPwdDisable('');
      await refreshSuperAdmin();
    } catch (e) { toast.error(e.response?.data?.message || 'Erreur'); }
    finally { setLoading(false); }
  };

  const secretFormate = secret.replace(/(.{4})/g, '$1 ').trim();

  return (
    <Carte icon={ShieldCheck} titre="Double authentification (2FA)"
      sousTitre="Protège le compte Super Admin avec un code à usage unique d'une application d'authentification.">
      {etat === 'codes' ? (
        <div className="max-w-md">
          <div className="flex items-center gap-2 text-green-600 mb-3">
            <CheckCircle2 size={18} /> <span className="font-semibold">2FA activée</span>
          </div>
          <p className="text-sm text-slate-600 mb-2">
            Conserve ces <strong>codes de secours</strong> en lieu sûr. Chacun fonctionne
            <strong> une seule fois</strong> et permet de se connecter en cas de perte du téléphone.
            <span className="text-amber-600"> Ils ne seront plus affichés.</span>
          </p>
          <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3 font-mono text-sm text-slate-900">
            {codes.map((c) => <div key={c} className="text-center tracking-wider">{c}</div>)}
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => copier(codes.join('\n'))}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg">
              <Copy size={14} /> Copier les codes
            </button>
            <button onClick={() => { setEtat('idle'); setSecret(''); setCodes([]); }}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
              J'ai noté mes codes
            </button>
          </div>
        </div>
      ) : etat === 'setup' ? (
        <form onSubmit={verifier} className="max-w-md space-y-4">
          {uri && (
            <div className="flex flex-col items-center gap-2">
              <div className="bg-white border border-slate-200 p-3 rounded-xl"><QRCodeSVG value={uri} size={168} /></div>
              <p className="text-xs text-slate-500 text-center">Scanne ce QR code dans ton application d'authentification — ou, si tu ne peux pas scanner, saisis la clé ci-dessous.</p>
            </div>
          )}
          <ol className="text-sm text-slate-600 space-y-1.5 list-decimal list-inside">
            <li>Ouvre ton application d'authentification.</li>
            <li>Scanne le QR code ci-dessus (ou « Ajouter un compte » → « Saisir une clé de configuration »).</li>
            <li>Le nom du compte est ton email.</li>
          </ol>
          <Champ label="Clé de configuration">
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-slate-100 border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-900 font-mono tracking-wider break-all">
                {secretFormate}
              </code>
              <button type="button" onClick={() => copier(secret)}
                className="p-2.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg shrink-0" title="Copier la clé">
                <Copy size={16} />
              </button>
            </div>
          </Champ>
          <Champ label="Code à 6 chiffres affiché par l'application">
            <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric" placeholder="000000"
              className={champCls2fa + ' tracking-[0.4em] text-center'} autoFocus />
          </Champ>
          <div className="flex gap-2">
            <button type="button" onClick={() => { setEtat('idle'); setSecret(''); }}
              className="px-4 py-2.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl">Annuler</button>
            <button type="submit" disabled={loading}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-xl disabled:opacity-50">
              {loading && <Loader size={16} className="animate-spin" />} Activer la 2FA
            </button>
          </div>
        </form>
      ) : actif ? (
        <div className="max-w-md">
          <div className="flex items-center gap-2 text-green-600 mb-3">
            <CheckCircle2 size={18} /> <span className="font-semibold">Activée</span>
            <span className="text-sm text-slate-500">— un code est demandé à chaque connexion.</span>
          </div>
          {showDisable ? (
            <form onSubmit={desactiver} className="space-y-3">
              <Champ label="Confirme avec ton mot de passe pour désactiver">
                <input type="password" value={pwdDisable} onChange={e => setPwdDisable(e.target.value)}
                  className={champCls2fa} autoComplete="current-password" autoFocus />
              </Champ>
              <div className="flex gap-2">
                <button type="button" onClick={() => { setShowDisable(false); setPwdDisable(''); }}
                  className="px-4 py-2.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl">Annuler</button>
                <button type="submit" disabled={loading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-red-600 hover:bg-red-700 text-white rounded-xl disabled:opacity-50">
                  {loading && <Loader size={16} className="animate-spin" />} Désactiver la 2FA
                </button>
              </div>
            </form>
          ) : (
            <button onClick={() => setShowDisable(true)}
              className="px-4 py-2.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border border-slate-300">
              Désactiver la 2FA
            </button>
          )}
        </div>
      ) : (
        <div className="max-w-md">
          <p className="text-sm text-slate-500 mb-4">
            Le compte Super Admin contrôle toute la plateforme : la 2FA est fortement recommandée.
            En cas de perte de ton téléphone, tes codes de secours te dépannent ; en dernier recours,
            un administrateur technique peut réinitialiser ta 2FA depuis le serveur.
          </p>
          <button onClick={demarrer} disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-xl disabled:opacity-50">
            {loading ? <Loader size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            Activer la 2FA
          </button>
        </div>
      )}
    </Carte>
  );
}

export default function MonProfilSuperAdmin() {
  const { superAdmin, refreshSuperAdmin } = useSuperAdminAuth();

  const [infos, setInfos] = useState({
    prenom: superAdmin?.prenom || '',
    nom: superAdmin?.nom || '',
    email: superAdmin?.email || '',
    telephone: superAdmin?.telephone || '',
  });
  const [savingInfos, setSavingInfos] = useState(false);

  const [pwd, setPwd] = useState({ ancien: '', nouveau: '', confirm: '' });
  const [savingPwd, setSavingPwd] = useState(false);

  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef(null);

  const photoUrl = resolveUploadUrl(superAdmin?.photo_url);
  const ini = initiales(superAdmin?.prenom, superAdmin?.nom);

  const handleInfos = e => setInfos(f => ({ ...f, [e.target.name]: e.target.value }));

  const enregistrerInfos = async e => {
    e.preventDefault();
    if (!infos.prenom.trim() || !infos.nom.trim()) {
      return toast.error('Le prénom et le nom sont requis');
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(infos.email.trim())) {
      return toast.error('Adresse email invalide');
    }
    setSavingInfos(true);
    try {
      await superAdminApi.put('/superadmin/auth/me', {
        prenom: infos.prenom.trim(),
        nom: infos.nom.trim(),
        email: infos.email.trim(),
        telephone: infos.telephone || '',
      });
      await refreshSuperAdmin();
      toast.success('Profil mis à jour');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingInfos(false);
    }
  };

  const changerMotDePasse = async e => {
    e.preventDefault();
    if (pwd.nouveau !== pwd.confirm) {
      return toast.error('Les nouveaux mots de passe ne correspondent pas');
    }
    if (pwd.nouveau.length < 8) {
      return toast.error('Le mot de passe doit faire au moins 8 caractères');
    }
    setSavingPwd(true);
    try {
      await superAdminApi.post('/superadmin/auth/change-password', {
        ancienMotDePasse: pwd.ancien,
        nouveauMotDePasse: pwd.nouveau,
      });
      toast.success('Mot de passe modifié');
      setPwd({ ancien: '', nouveau: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors du changement');
    } finally {
      setSavingPwd(false);
    }
  };

  const choisirPhoto = () => fileRef.current?.click();

  const uploaderPhoto = async e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      return toast.error('Image trop volumineuse (2 Mo max)');
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      await superAdminApi.post('/superadmin/auth/me/photo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshSuperAdmin();
      toast.success('Photo mise à jour');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const supprimerPhoto = async () => {
    setUploadingPhoto(true);
    try {
      await superAdminApi.delete('/superadmin/auth/me/photo');
      await refreshSuperAdmin();
      toast.success('Photo supprimée');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Mon Profil</h1>
        <p className="text-slate-500 text-sm mt-1">Gère tes informations et la sécurité de ton compte Super Admin</p>
      </div>

      {/* Hero */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
          <div className="relative shrink-0">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={`${superAdmin?.prenom} ${superAdmin?.nom}`}
                className="w-24 h-24 rounded-full object-cover border-2 border-slate-200"
              />
            ) : (
              <div className="w-24 h-24 rounded-full flex items-center justify-center text-2xl font-bold border-2 border-blue-100 bg-blue-50 text-blue-600">
                {ini}
              </div>
            )}
            <button
              onClick={choisirPhoto}
              disabled={uploadingPhoto}
              title="Changer la photo"
              className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full flex items-center justify-center text-white bg-blue-600 hover:bg-blue-700 shadow-lg disabled:opacity-60"
            >
              {uploadingPhoto ? <Loader size={16} className="animate-spin" /> : <Camera size={16} />}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={uploaderPhoto}
              className="hidden"
            />
          </div>

          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-xl font-bold text-slate-900">
              {superAdmin?.prenom} {superAdmin?.nom}
            </h2>
            <div className="flex items-center justify-center sm:justify-start gap-2 mt-1.5">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                <ShieldCheck size={12} /> Super Admin
              </span>
              {superAdmin?.deux_facteurs_actif && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                  2FA activée
                </span>
              )}
            </div>
            <div className="mt-3 space-y-1 text-sm text-slate-500">
              <p className="flex items-center justify-center sm:justify-start gap-2">
                <Mail size={14} /> {superAdmin?.email}
              </p>
              {superAdmin?.telephone && (
                <p className="flex items-center justify-center sm:justify-start gap-2">
                  <Phone size={14} /> {superAdmin.telephone}
                </p>
              )}
            </div>
            {photoUrl && (
              <button
                onClick={supprimerPhoto}
                disabled={uploadingPhoto}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 disabled:opacity-50"
              >
                <Trash2 size={13} /> Supprimer la photo
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Infos personnelles */}
      <Carte icon={User} titre="Informations personnelles" sousTitre="Identité de ton compte Super Admin">
        <form onSubmit={enregistrerInfos} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Champ label="Prénom">
              <input name="prenom" value={infos.prenom} onChange={handleInfos} className={inputCls} placeholder="Prénom" />
            </Champ>
            <Champ label="Nom">
              <input name="nom" value={infos.nom} onChange={handleInfos} className={inputCls} placeholder="Nom" />
            </Champ>
          </div>
          <Champ label="Adresse email">
            <input name="email" type="email" value={infos.email} onChange={handleInfos} className={inputCls} placeholder="email@gmail.com" />
          </Champ>
          <Champ label="Téléphone">
            <PhoneInput
              light
              value={infos.telephone}
              onChange={e => setInfos(f => ({ ...f, telephone: e.target.value }))}
            />
          </Champ>
          <button
            type="submit"
            disabled={savingInfos}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold transition disabled:opacity-50"
          >
            {savingInfos ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            {savingInfos ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </form>
      </Carte>

      {/* Sécurité */}
      <Carte icon={KeyRound} titre="Sécurité" sousTitre="Change ton mot de passe (8 caractères minimum). L'ancien est demandé.">
        <form onSubmit={changerMotDePasse} className="space-y-4 max-w-md">
          <Champ label="Mot de passe actuel">
            <input type="password" value={pwd.ancien} onChange={e => setPwd(p => ({ ...p, ancien: e.target.value }))} className={inputCls} autoComplete="current-password" />
          </Champ>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Champ label="Nouveau mot de passe">
              <input type="password" value={pwd.nouveau} onChange={e => setPwd(p => ({ ...p, nouveau: e.target.value }))} className={inputCls} autoComplete="new-password" />
            </Champ>
            <Champ label="Confirmer">
              <input type="password" value={pwd.confirm} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))} className={inputCls} autoComplete="new-password" />
            </Champ>
          </div>
          <button
            type="submit"
            disabled={savingPwd}
            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white px-5 py-2.5 rounded-xl font-semibold transition disabled:opacity-50"
          >
            {savingPwd ? <Loader size={16} className="animate-spin" /> : <KeyRound size={16} />}
            {savingPwd ? 'Modification…' : 'Changer le mot de passe'}
          </button>
        </form>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6 pt-5 border-t border-slate-200">
          <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
            <Clock size={18} className="text-slate-400 shrink-0" />
            <div>
              <p className="text-xs text-slate-500">Dernière connexion</p>
              <p className="text-sm text-slate-800">{formatDate(superAdmin?.derniere_connexion)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
            <CalendarDays size={18} className="text-slate-400 shrink-0" />
            <div>
              <p className="text-xs text-slate-500">Compte créé le</p>
              <p className="text-sm text-slate-800">{formatDate(superAdmin?.created_at)}</p>
            </div>
          </div>
        </div>
      </Carte>

      {/* Double authentification (2FA) */}
      <Securite2FASA />
    </div>
  );
}
